import { financialManagementAccountList } from 'apis/fms';
import { TaxRegime } from 'constants/france-tax';
import { useEffect, useMemo, useState } from 'react';
import { FmsAccountManagement } from 'types/fms';
import {
  formatAmount,
  formatInvoiceDate,
  InvoiceLayout,
  OrderDetailBatchReturnType,
  useInvoiceRecipientAddress,
} from './common';

const FRANCE_VAT_RATE = 0.2;
const HEADER_BLUE = '#1d4ed8';

const InvoiceColGroup = () => (
  <colgroup>
    <col className="w-[48px]" />
    <col />
    <col className="w-[110px]" />
    <col className="w-[120px]" />
  </colgroup>
);

/** EUR 含税价拆税，保留两位小数（与法票样例一致） */
export function extractTaxFromGrossEur(
  grossAmount: number,
  rate = FRANCE_VAT_RATE,
) {
  return Math.round(((grossAmount * rate) / (1 + rate)) * 100) / 100;
}

export function extractNetFromGrossEur(
  grossAmount: number,
  rate = FRANCE_VAT_RATE,
) {
  return (
    Math.round(
      (grossAmount - extractTaxFromGrossEur(grossAmount, rate)) * 100,
    ) / 100
  );
}

const PAYMENT_LABEL_FR: Record<string, string> = {
  STRIPE_FRANCE_WEBSITE: 'Carte de crédit',
  STRIPE: 'Carte de crédit',
  CREDIT_CARD: 'Carte de crédit',
  CARD: 'Carte de crédit',
  CASH: 'Espèces',
  BANK_TRANSFER: 'Virement bancaire',
  BANK: 'Virement bancaire',
};

const normalizeKey = (value?: string | number | null) => {
  if (value === undefined || value === null) return '';
  return String(value).replace(/\s+/g, '').toUpperCase();
};

const resolvePaymentLabelFr = (
  payType: string,
  accounts: FmsAccountManagement[],
) => {
  const key = normalizeKey(payType);
  if (PAYMENT_LABEL_FR[key]) return PAYMENT_LABEL_FR[key];

  for (const [prefix, label] of Object.entries(PAYMENT_LABEL_FR)) {
    if (key.includes(prefix)) return label;
  }

  const account = accounts.find(
    (item) => normalizeKey(item.accountCode) === key,
  );
  return account?.accountName || payType;
};

const formatQuantityFr = (quantity: number) => {
  const unit = quantity > 1 ? 'unités' : 'unité';
  return `${quantity} ${unit}`;
};

const FranceInvoiceHeader = () => {
  return (
    <header className="mb-5 text-center" style={{ color: HEADER_BLUE }}>
      <div className="text-[15px] font-bold mb-0.5">SASU XIAOMA PARIS</div>
      <div className="text-xs mb-0.5">43 RUE DE LIEGE</div>
      <div className="text-xs mb-0.5">75008 PARIS – France</div>
      <div className="text-xs mb-4">Tel : +33 6 75 26 13 96</div>
      <div className="text-[28px] font-bold text-black">Facture</div>
    </header>
  );
};

type FranceInvoiceProps = {
  order: OrderDetailBatchReturnType;
  /** STANDARD_TVA / EXPORT_AIRPORT → tva；MARGIN → aucune */
  variant: 'tva' | 'aucune';
};

const FranceInvoice = ({ order, variant }: FranceInvoiceProps) => {
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);
  const { cityLabel, countryLabel } = useInvoiceRecipientAddress(order);

  const totalTtc = order.payAmountActualCurrency ?? 0;
  const actualCurrencyRate = order.orderItems[0]?.actualCurrencyRate ?? 1;
  const freightAmount = order.freightAmountActualCurrency ?? 0;
  const discountAmount = order.promotionAmountActualCurrency ?? 0;
  const pointsDeduction = order.useIntegration
    ? Math.round(order.useIntegration * actualCurrencyRate)
    : 0;

  /** 运费含税价按标准 20% 拆税（与后端 france-order-tax 一致；MARGIN 商品税仍对顾客隐藏） */
  const freightTva =
    freightAmount > 0 ? extractTaxFromGrossEur(freightAmount) : 0;

  /**
   * STANDARD / EXPORT：展示整单 20% 税额
   * MARGIN：商品税对顾客隐藏（Aucune）；有运费时额外标明运费 20% TVA
   */
  const taxRows =
    variant === 'tva'
      ? [
          {
            taux: '20%',
            montantLabel: 'Montant TVA :',
            montant: formatAmount(
              order.totalTaxAmount ?? extractTaxFromGrossEur(totalTtc),
            ),
          },
        ]
      : freightAmount > 0
        ? [
            {
              taux: 'Aucune',
              montantLabel: 'Montant TVA :',
              montant: '—',
            },
            {
              taux: '20% (Frais de port)',
              montantLabel: 'Montant TVA :',
              montant: formatAmount(freightTva),
            },
          ]
        : [
            {
              taux: 'Aucune',
              montantLabel: 'Montant TVA :',
              montant: '—',
            },
          ];

  const summaryRows = [
    freightAmount > 0
      ? {
          label: 'Frais de port :',
          value: formatAmount(freightAmount),
          emphasize: false,
        }
      : null,
    discountAmount
      ? {
          label: 'Remise :',
          value: `-${formatAmount(discountAmount)}`,
          emphasize: false,
        }
      : null,
    pointsDeduction
      ? {
          label: 'Points :',
          value: `-${formatAmount(pointsDeduction)}`,
          emphasize: false,
        }
      : null,
    {
      label: 'Total TTC :',
      value: formatAmount(totalTtc),
      emphasize: true,
    },
  ].filter((row): row is { label: string; value: string; emphasize: boolean } =>
    Boolean(row),
  );

  useEffect(() => {
    if (!order.createdFrom) return;
    financialManagementAccountList(order.createdFrom).then((res) => {
      setPaymentAccountList([...res].sort((a, b) => a.sort - b.sort));
    });
  }, [order.createdFrom]);

  const paymentLines = useMemo(() => {
    return (order.orderPayList || [])
      .filter((payment) => (payment.payAmount ?? 0) > 0)
      .map((payment) => ({
        label: resolvePaymentLabelFr(payment.payType, paymentAccountList),
        amount: payment.payAmount,
      }));
  }, [order.orderPayList, paymentAccountList]);

  const cityCountryLine = [cityLabel, countryLabel].filter(Boolean).join(' – ');

  return (
    <InvoiceLayout>
      <div className="flex h-full flex-col text-xs text-black">
        <FranceInvoiceHeader />

        <section className="mb-5 flex items-start justify-between gap-8">
          <div className="min-w-0 flex-1 leading-[1.7]">
            <div className="font-bold mb-1">Client ou Cliente</div>
            <div>{order.receiverName}</div>
            {order.receiverDetailAddress ? (
              <div>{order.receiverDetailAddress}</div>
            ) : null}
            {cityCountryLine ? <div>{cityCountryLine}</div> : null}
          </div>

          <div className="shrink-0 text-right leading-[1.8]">
            <div>
              <span className="font-bold">Facture N° :</span>{' '}
              {order.invoiceNo || '—'}
            </div>
            <div>
              <span className="font-bold">Date d&apos;émission :</span>{' '}
              {formatInvoiceDate(order)}
            </div>
            <div>
              <span className="font-bold">Type de vente :</span> Livraisons de
              biens
            </div>
          </div>
        </section>

        <div className="mb-5 min-h-0 flex-1">
          <table className="h-full w-full border-collapse table-fixed">
            <InvoiceColGroup />
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black p-2 text-center font-bold">
                  N°
                </th>
                <th className="border border-black p-2 text-center font-bold">
                  Produits
                </th>
                <th className="border border-black p-2 text-center font-bold">
                  Qté
                </th>
                <th className="border border-black p-2 text-center font-bold">
                  Prix u. (€)
                </th>
              </tr>
            </thead>
            <tbody>
              {order.orderItems.map((item, index) => (
                <tr key={item.id}>
                  <td className="border border-black h-[34px] text-center align-middle">
                    {index + 1}
                  </td>
                  <td className="border border-black px-3 py-1.5 align-middle">
                    {item.productId} {item.productName}
                  </td>
                  <td className="border border-black px-2 py-1.5 text-center align-middle">
                    {formatQuantityFr(item.productQuantity)}
                  </td>
                  <td className="border border-black px-3 py-1.5 text-right align-middle tabular-nums">
                    {formatAmount(item.productPriceActualCurrency)}
                  </td>
                </tr>
              ))}

              <tr style={{ height: '100%' }}>
                <td className="border border-black p-0" />
                <td className="border border-black p-0" />
                <td className="border border-black p-0" />
                <td className="border border-black p-0" />
              </tr>

              {summaryRows.map((row) => (
                <tr key={row.label}>
                  <td className="border border-black bg-white">&nbsp;</td>
                  <td className="border border-black bg-white">&nbsp;</td>
                  <td
                    className={`border border-black px-2 py-2 text-right align-middle ${
                      row.emphasize ? 'font-bold bg-gray-200' : ''
                    }`}
                  >
                    {row.label}
                  </td>
                  <td
                    className={`border border-black px-3 py-2 text-right align-middle tabular-nums ${
                      row.emphasize ? 'font-bold bg-gray-200' : ''
                    }`}
                  >
                    {row.value}
                  </td>
                </tr>
              ))}

              {taxRows.map((row, index) => (
                <tr key={`${row.taux}-${index}`}>
                  <td className="border border-black px-2 py-2 font-bold align-middle">
                    {index === 0 ? 'Taux' : '\u00a0'}
                  </td>
                  <td className="border border-black px-2 py-2 align-middle">
                    {row.taux}
                  </td>
                  <td className="border border-black px-2 py-2 text-right align-middle">
                    {row.montantLabel}
                  </td>
                  <td className="border border-black px-3 py-2 text-right align-middle tabular-nums">
                    {row.montant}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="mb-6 flex items-start justify-between gap-6">
          <div className="min-w-[260px] leading-[1.7]">
            <div className="font-bold mb-1">Paiement</div>
            {paymentLines.length ? (
              paymentLines.map((line, index) => (
                <div key={`${line.label}-${index}`}>
                  {line.label} : {formatAmount(line.amount)}
                </div>
              ))
            ) : (
              <div>Moyen de paiement : —</div>
            )}
          </div>

          <div className="shrink-0 text-right leading-[1.7]">
            <div className="whitespace-nowrap font-bold text-[13px]">
              ITEMS SOLD CANNOT BE RETURNED OR EXCHANGED
            </div>
            {variant === 'aucune' ? (
              <div className="mt-2 text-right text-[11px] leading-[1.3]">
                <div>
                  Régime particulier — Biens d&apos;occasion — TVA sur marge
                </div>
                <div>
                  Article 297 A du CGI et article 313 de la Directive
                  2006/112/CE.
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <footer className="text-[11px] leading-[1.7]">
          <div className="mb-1">
            XIAOMA PARIS | SAS, société par actions simplifiée au capital social
            de 100 000,00 €
          </div>
          <div className="flex justify-between gap-4">
            <span>Siren: 988 122 420</span>
            <span>N° de TVA FR33988122420</span>
          </div>
        </footer>
      </div>
    </InvoiceLayout>
  );
};

export const resolveFranceInvoiceVariant = (
  taxRegime?: string | null,
): 'tva' | 'aucune' => {
  if (taxRegime === TaxRegime.MARGIN) return 'aucune';
  return 'tva';
};

export default FranceInvoice;
